import { Key, useEffect, useMemo, useState } from 'react';
import { Drawer, DrawerProps, Space } from 'antd';
import Button from 'components/new-ui/base/button/Button';
import { ListPayload } from 'core/http/response';
import useFormUtils from 'hooks/useFormUtils';
import useItemFieldTemplates from 'hooks/useItemFieldTemplates';
import { DeclarationExternalEntity } from 'store/declarations/enums/common/declaration-external-entity';
import { DeclarationInternalType } from 'store/declarations/enums/common/declaration-internal-type';
import { ItemFieldTemplateRow } from 'store/item-field-templates/itemFieldTemplate';
import TemplateSearchInput from 'views/templates/components/TemplateSearchInput';
import toItemFieldTemplateRow from 'views/templates/item-field-templates/toItemFieldTemplateRow';
import getItemFieldTemplateViewPath from './getItemFieldTemplateViewPath';
import Table from './Table';

interface Props extends DrawerProps {
    // The drawer shows a spinner on Apply while this runs
    onApply: (templateId: string) => void | Promise<void>;
}

const DEFAULT_PAGE_SIZE = 10;

const ApplyItemTemplateDrawer = ({ onApply, onClose, ...rest }: Props) => {
    const { countryLowerCase: country, externalEntity, internalType, formType } = useFormUtils();
    const { templates, isLoading, listTemplates } = useItemFieldTemplates();

    const [selectedTemplateId, setSelectedTemplateId] = useState<Key>();
    const [isApplying, setIsApplying] = useState(false);
    const [query, setQuery] = useState('');
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    useEffect(() => {
        if (!externalEntity || !internalType || !formType) return;

        listTemplates({
            declarationExternalEntity: externalEntity as DeclarationExternalEntity,
            declarationInternalType: internalType as DeclarationInternalType,
            declarationName: formType,
            pageNumber,
            pageSize,
            query,
        });
    }, [listTemplates, externalEntity, internalType, formType, pageNumber, pageSize, query]);

    const data: ListPayload<ItemFieldTemplateRow> = useMemo(
        () => ({
            list: templates?.list.map(toItemFieldTemplateRow) ?? [],
            pageNumber,
            pageSize,
            total: templates?.total ?? 0,
        }),
        [templates, pageNumber, pageSize]
    );

    const getTemplatePath = (id: string) => getItemFieldTemplateViewPath({ country, internalType, formType, id });

    const handleSearch = (newQuery: string) => {
        setQuery(newQuery);
        setPageNumber(0);
    };

    const handlePageChange = (newPageNumber: number, newPageSize: number) => {
        setPageNumber(newPageNumber);
        setPageSize(newPageSize);
    };

    const handleApply = async () => {
        if (selectedTemplateId === undefined) return;

        setIsApplying(true);
        try {
            await onApply(String(selectedTemplateId));
        } finally {
            setIsApplying(false);
        }
    };

    return (
        <Drawer title="Item Fields Templates" width={612} onClose={onClose} {...rest}>
            <Space direction="vertical" size={24} style={{ width: '100%' }}>
                <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <TemplateSearchInput onSearch={handleSearch} />
                    <Button disabled={selectedTemplateId === undefined} loading={isApplying} onClick={handleApply}>
                        Apply
                    </Button>
                </header>
                <Table
                    data={data}
                    getTemplatePath={getTemplatePath}
                    loading={isLoading}
                    selectedRowKey={selectedTemplateId}
                    onSelectedRowKeyChange={setSelectedTemplateId}
                    onPageChange={handlePageChange}
                />
            </Space>
        </Drawer>
    );
};

export default ApplyItemTemplateDrawer;
